// "What's new": the popup a player sees once after an update (client/whatsnew.ts).
//
// Adding one for a release: put a new entry at the TOP of WHATS_NEW, with art from that release.
// - id: unique and never reused, e.g. '2026-11-16-lantern-night'. A new id is what makes the popup show again.
// - hero: a wide painted banner (about 1200x320, plus an @2x), as an art id from public/art/manifest.json.
// - features: the 2 to 4 biggest changes only. Title a few words, text one short line. No rules or numbers
//   walls: detail lives in Help. `go` is an in-game address (#/pass, #/market…) the card opens.
// - art for a feature: { creature: '<species or species~skin>' }, { art: '<id in public/art>' } or
//   { outfit: '<id in shared/outfits.ts>' } (shown standing, in the player's own look).
// Admin > Dev tools > What's new previews the newest entry before it ships. Every trainer sees only the newest
// entry, once (settings.newsSeen); trainers made after it shipped never see it.

export type NewsArt = { creature: string } | { art: string } | { outfit: string };

export interface NewsFeature {
  title: string;
  text: string;
  art: NewsArt;
  /** An in-game address, like '#/pass'. */
  go?: string;
}

export interface NewsEntry {
  id: string;
  /** Release day, YYYY-MM-DD (shown as "16 Nov"). */
  date: string;
  title: string;
  /** One short line under the title. */
  tagline?: string;
  /** Wide banner art id (public/art/<id>.png, and <id>@2x.png when drawn). */
  hero: string;
  /** The hero's showpiece, drawn large over the banner's right side. */
  heroArt?: NewsArt;
  features: NewsFeature[];
}

/** Newest first. Empty until the first update after launch. */
export const WHATS_NEW: NewsEntry[] = [];

/**
 * The layout sample: Admin previews it while WHATS_NEW is empty, and the tests check it like a real entry.
 * Built from Season 2 art so the popup can be judged with real pictures. Never shown to players.
 */
export const NEWS_SAMPLE: NewsEntry = {
  id: 'sample',
  date: '2026-11-16',
  title: 'Lantern Night',
  tagline: 'Season 2 of the pass lights up Yomi.',
  hero: 'pass-lanternnight-banner',
  heroArt: { creature: 'ninefold~lanternnight' },
  features: [
    { title: 'A new pass', text: '50 tiers of Yomi rewards, through 21 December.', art: { art: 'icon-pass-plus-lanternnight' }, go: '#/pass' },
    { title: 'Lantern Robes', text: 'A festival kimono and fox mask at tier 20.', art: { outfit: 'lantern-robes' }, go: '#/pass' },
    { title: 'Paper Lantern', text: 'A lit lantern beside your name in chat.', art: { art: 'icon-badge-paper-lantern' } },
  ],
};

export function latestNews(): NewsEntry | undefined { return WHATS_NEW[0]; }
export function isNewsId(id: unknown): id is string { return typeof id === 'string' && WHATS_NEW.some((e) => e.id === id); }
